import { dispatchEvent } from '@packages/events/lib/dispatchEvent';
import { call } from '@packages/functions';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

type CallbackFn = (e: Event) => any;

interface Options {
    element: MutableRefObject<Element | Window | Document | null>;
    onChange: CallbackFn;
}

interface ScrollState {
    x: number;
    y: number;
    clientRect?: DOMRect | null;
}

interface ScrollMethods {
    element: Options['element'];

    scrollTo(x: number, y: number): void;

    scrollTop(y: number): void;

    scrollLeft(x: number): void;
}

const getElement = (el: Options['element']): Element => el.current as Element;

export const useScroll = (options: Partial<Options> = {}): ScrollState & ScrollMethods => {
    const [state, setState] = useState<ScrollState>({
        x: 0,
        y: 0,

        clientRect: null,
    });

    const windowRef = useRef(typeof window !== 'undefined' ? window : null);
    const { element, onChange } = {
        element: windowRef,
        ...(options || {}),
    };

    const handleScroll = useCallback(
        (e: Event) => {
            if (element.current) {
                const el = getElement(element);
                setState({
                    x: el.scrollLeft || (el as any).scrollX || 0,
                    y: el.scrollTop || (el as any).scrollY || 0,
                    clientRect: call(el.getBoundingClientRect) || undefined,
                });
            }

            call(onChange, e);
        },
        [onChange, element.current],
    );

    const scrollLeft = useCallback(
        (x: number) => {
            if (element.current) {
                getElement(element).scrollLeft = x;
            }
        },
        [element.current],
    );

    const scrollTop = useCallback(
        (y: number) => {
            if (element.current) {
                getElement(element).scrollTop = y;
            }
        },
        [element.current],
    );

    const scrollTo = useCallback(
        (x: number, y: number) => {
            if (element.current) {
                getElement(element).scrollLeft = x;
                getElement(element).scrollTop = y;
            }
        },
        [element.current],
    );

    useEffect(() => {
        if (element.current) {
            getElement(element).addEventListener('scroll', handleScroll);
            dispatchEvent(getElement(element), 'scroll');
        }

        return () => {
            if (element.current) {
                getElement(element).removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll, element.current]);

    return {
        ...state,
        element,
        scrollTo,
        scrollTop,
        scrollLeft,
    };
};
